import React, { useEffect, useMemo, useState } from 'react';
import PageMenuButton from '../components/PageMenuButton';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Stack,
  TextField,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
} from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import SettingsIcon from '@mui/icons-material/Settings';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import KeyRoundedIcon from '@mui/icons-material/KeyRounded';
import ShareRoundedIcon from '@mui/icons-material/ShareRounded';
import SyncLockRoundedIcon from '@mui/icons-material/SyncLockRounded';
import { buildCatalogFromProducts, fetchInventoryProductsWithOverrides, readSharedProductCatalog } from '../utils/catalog';
import { getApiUrl } from '../utils/api';
import { apiFetch } from '../utils/apiFetch';

const API_URL = getApiUrl();

const ensureShowInKds = (value) => value !== false;

const uniqueCatalogValues = (entries = {}) => {
  const seen = new Set();
  return Object.entries(entries).reduce((acc, [key, item]) => {
    const identity = `${item?.merchantId || ''}::${item?.externalCode || key}`;
    if (seen.has(identity)) return acc;
    seen.add(identity);
    acc.push(item);
    return acc;
  }, []);
};

const Kds = () => {
  const [catalog, setCatalog] = useState({ products: {}, options: {} });
  const [deviceConfig, setDeviceConfig] = useState(null);
  const [deviceConfigLoading, setDeviceConfigLoading] = useState(true);
  const [deviceConfigError, setDeviceConfigError] = useState('');
  const [rotateDialogOpen, setRotateDialogOpen] = useState(false);
  const [rotatingPin, setRotatingPin] = useState(false);
  const [feedback, setFeedback] = useState('');

  useEffect(() => {
    let active = true;

    const loadDeviceConfig = async () => {
      try {
        const response = await apiFetch(`${API_URL}/api/kds/device/config`);
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.success === false || !data.pin) {
          throw new Error(data.message || data.error || 'Falha ao carregar o acesso do KDS.');
        }
        if (active) {
          setDeviceConfig(data);
          setDeviceConfigError('');
        }
      } catch (err) {
        if (active) setDeviceConfigError(err.message || 'Falha ao carregar o acesso do KDS.');
      } finally {
        if (active) setDeviceConfigLoading(false);
      }
    };

    loadDeviceConfig();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    const loadCatalog = async () => {
      try {
        const items = await fetchInventoryProductsWithOverrides();
        if (!active) return;
        setCatalog(buildCatalogFromProducts(items));
      } catch (err) {
        console.error('Falha ao ler catálogo para o KDS', err);
        try {
          const stored = readSharedProductCatalog();
          if (!active) return;
          setCatalog({
            products: stored.products || {},
            options: stored.options || {},
          });
        } catch {
          if (!active) return;
          setCatalog({ products: {}, options: {} });
        }
      }
    };

    loadCatalog();

    return () => {
      active = false;
    };
  }, []);

  const visibleProducts = useMemo(() => uniqueCatalogValues(catalog.products).filter((p) => ensureShowInKds(p.showInKds)), [catalog.products]);
  const hiddenProducts = useMemo(() => uniqueCatalogValues(catalog.products).filter((p) => !ensureShowInKds(p.showInKds)), [catalog.products]);
  const visibleOptions = useMemo(() => uniqueCatalogValues(catalog.options).filter((o) => ensureShowInKds(o.showInKds)), [catalog.options]);
  const hiddenOptions = useMemo(() => uniqueCatalogValues(catalog.options).filter((o) => !ensureShowInKds(o.showInKds)), [catalog.options]);
  const accessLink = typeof window === 'undefined'
    ? '/kds/acesso'
    : `${window.location.origin}${deviceConfig?.accessPath || '/kds/acesso'}`;
  const pinDigits = String(deviceConfig?.pin || '').slice(0, 6).split('');
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const copyValue = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      setFeedback(`${label} copiado.`);
    } catch {
      setFeedback(`Não foi possível copiar ${label.toLowerCase()}.`);
    }
  };

  const shareAccess = async () => {
    try {
      await navigator.share({
        title: 'Acesso ao KDS',
        text: `Abra o KDS e use o PIN ${deviceConfig.pin}.`,
        url: accessLink,
      });
    } catch (err) {
      if (err?.name !== 'AbortError') setFeedback('Não foi possível compartilhar o acesso.');
    }
  };

  const rotatePin = async () => {
    setRotatingPin(true);
    try {
      const response = await apiFetch(`${API_URL}/api/kds/device/config/rotate`, { method: 'POST' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false || !data.pin) {
        throw new Error(data.message || data.error || 'Falha ao gerar um novo PIN.');
      }
      setDeviceConfig(data);
      setDeviceConfigError('');
      setRotateDialogOpen(false);
      setFeedback('Novo PIN gerado. Os aparelhos anteriores foram desconectados.');
    } catch (err) {
      setDeviceConfigError(err.message || 'Falha ao gerar um novo PIN.');
      setRotateDialogOpen(false);
    } finally {
      setRotatingPin(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      p: { xs: 1.5, md: 2 },
      background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)',
      color: '#0f172a',
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <PageMenuButton />
            <Typography variant="h4" sx={{ fontWeight: 800 }}>Central do KDS</Typography>
          </Stack>
          <Typography variant="body2" sx={{ color: '#475569' }}>
            Conecte a cozinha, controle a visibilidade dos itens e abra o painel de monitoramento.
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<SettingsIcon />}
            component={RouterLink}
            to="/cardapio"
          >
            Ajustar no Cardápio
          </Button>
          <Button
            variant="contained"
            endIcon={<LaunchIcon />}
            component={RouterLink}
            to="/kds/monitor"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)' }}
          >
            Abrir monitor KDS
          </Button>
        </Stack>
      </Box>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: { xs: 2, md: 3 }, background: '#ffffff', border: '1px solid #c7d2fe', borderRadius: 3, boxShadow: '0 10px 30px rgba(15,23,42,0.08)' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <KeyRoundedIcon sx={{ color: '#4f46e5' }} />
              <Typography variant="h6">Conectar uma tela à cozinha</Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: '#475569', mb: 2 }}>
              Envie o link para o aparelho da cozinha e informe o PIN abaixo. A tela ficará vinculada somente a esta operação.
            </Typography>

            {deviceConfigLoading ? (
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 3 }}>
                <CircularProgress size={24} />
                <Typography color="text.secondary">Carregando acesso do KDS…</Typography>
              </Stack>
            ) : deviceConfigError ? (
              <Alert severity="error">{deviceConfigError}</Alert>
            ) : (
              <Grid container spacing={2} alignItems="stretch">
                <Grid size={{ xs: 12, md: 7 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Link de acesso</Typography>
                  <TextField
                    fullWidth
                    value={accessLink}
                    slotProps={{ input: { readOnly: true } }}
                    inputProps={{ 'aria-label': 'Link público de acesso ao KDS' }}
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }}>
                    <Button
                      variant="outlined"
                      startIcon={<ContentCopyRoundedIcon />}
                      onClick={() => copyValue(accessLink, 'Link')}
                    >
                      Copiar link
                    </Button>
                    {canShare ? (
                      <Button variant="outlined" startIcon={<ShareRoundedIcon />} onClick={shareAccess}>
                        Compartilhar
                      </Button>
                    ) : null}
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 5 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.75 }}>PIN desta cozinha</Typography>
                  <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }} aria-label={`PIN ${deviceConfig.pin}`}>
                    {pinDigits.map((digit, index) => (
                      <Box
                        key={`${digit}-${index}`}
                        aria-hidden="true"
                        sx={{
                          width: { xs: 42, sm: 50 },
                          height: 58,
                          display: 'grid',
                          placeItems: 'center',
                          borderRadius: 2,
                          border: '2px solid #c7d2fe',
                          background: '#eef2ff',
                          color: '#312e81',
                          fontSize: '1.65rem',
                          fontWeight: 900,
                        }}
                      >
                        {digit}
                      </Box>
                    ))}
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }}>
                    <Button
                      variant="outlined"
                      startIcon={<ContentCopyRoundedIcon />}
                      onClick={() => copyValue(String(deviceConfig.pin), 'PIN')}
                    >
                      Copiar PIN
                    </Button>
                    <Button
                      variant="text"
                      color="warning"
                      startIcon={<SyncLockRoundedIcon />}
                      onClick={() => setRotateDialogOpen(true)}
                    >
                      Gerar novo PIN
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 3, background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 3, boxShadow: '0 10px 30px rgba(15,23,42,0.08)' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <PlaylistAddCheckIcon />
              <Typography variant="h6">Visibilidade no KDS</Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: '#475569', mb: 2 }}>
              Controle quais produtos e complementos aparecem no painel. Ajuste as opções no modal de cada item no cardápio.
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <Typography variant="subtitle2">Produtos</Typography>
                <Stack direction="row" spacing={1} alignItems="baseline">
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>{visibleProducts.length}</Typography>
                  <Typography variant="body2" sx={{ color: '#475569' }}>visíveis</Typography>
                </Stack>
                <Typography variant="caption" sx={{ color: '#475569' }}>
                  {hiddenProducts.length} ocultos
                </Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="subtitle2">Complementos</Typography>
                <Stack direction="row" spacing={1} alignItems="baseline">
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>{visibleOptions.length}</Typography>
                  <Typography variant="body2" sx={{ color: '#475569' }}>visíveis</Typography>
                </Stack>
                <Typography variant="caption" sx={{ color: '#475569' }}>
                  {hiddenOptions.length} ocultos
                </Typography>
              </Grid>
            </Grid>

            {(hiddenProducts.length > 0 || hiddenOptions.length > 0) && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" sx={{ color: '#b91c1c' }}>
                  Itens ocultos não serão enviados para o painel KDS.
                </Typography>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={rotateDialogOpen} onClose={() => !rotatingPin && setRotateDialogOpen(false)}>
        <DialogTitle>Gerar um novo PIN?</DialogTitle>
        <DialogContent>
          <Typography>
            Todos os aparelhos conectados com o PIN atual serão desconectados e precisarão informar o novo código.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRotateDialogOpen(false)} disabled={rotatingPin}>Cancelar</Button>
          <Button variant="contained" color="warning" onClick={rotatePin} disabled={rotatingPin}>
            {rotatingPin ? 'Gerando…' : 'Gerar e desconectar'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(feedback)}
        autoHideDuration={4000}
        onClose={() => setFeedback('')}
        message={feedback}
      />
    </Box>
  );
};

export default Kds;
